/**
 * Formato de fecha relativa al "ahora" para columnas operativas tipo
 * "Última actualización" en el panel técnico.
 *
 * Resoluciones:
 *   - 0 min        → "ahora"
 *   - 1-59 min     → "hace N min"
 *   - 1-23 h       → "hace N h"
 *   - mismo día anterior → "ayer HH:MM"
 *   - >1 día y <365 días → "DD mmm" (ej. "6 may")
 *   - >365 días    → "DD mmm YYYY"
 *
 * El segundo parámetro `ref` permite tests deterministas sin congelar
 * `Date.now`.
 */
export function fmtRelative(iso: string | null | undefined, ref: Date = new Date()): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';

  const diffMs = ref.getTime() - d.getTime();
  // Futuro o ahora mismo (clock skew, race con servidor) → "ahora".
  if (diffMs < 60_000) return 'ahora';

  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 60) return `hace ${diffMin} min`;

  const diffH = Math.floor(diffMs / 3_600_000);
  if (diffH < 24) return `hace ${diffH} h`;

  // ¿Es ayer (mismo "día calendario - 1" del ref)?
  const refMidnight = new Date(ref);
  refMidnight.setHours(0, 0, 0, 0);
  const yesterdayMidnight = new Date(refMidnight);
  yesterdayMidnight.setDate(yesterdayMidnight.getDate() - 1);
  if (d >= yesterdayMidnight && d < refMidnight) {
    const hhmm = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    return `ayer ${hhmm}`;
  }

  // Más de 1 día atrás. Si está dentro del año en curso, omite el año.
  const sameYear = d.getFullYear() === ref.getFullYear();
  return d.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}
